import { useCallback, useState } from 'react'
import { useCreateAccountOpeningMutation, useUpdateAccountOpeningMutation } from '../../../store/accountOpening/accountOpening'
import {
  type AccountOpeningCreateRequest,
  type AccountOpeningDTO,
  type AccountOpeningStatus,
  type AccountOpeningTypeSelector,
  type AccountType,
} from '../../../models/AccountOpening'
import {
  AccountOpeningCollections,
  type AccountOpeningDescriptor,
  type AccountOpeningStep,
  type AccountOpeningStepId,
} from '../../../models/AccountOpeningDescriptor'
import { axisValue, findAccountType } from '../domain/accountTypeCatalog'
import { storedOutcome, type SubmissionOutcome } from '../domain/connectorResult'
import { buildAccountsPayload, buildCustomersPayload, loadDraftIntoWizard, type LoadedDraft } from '../domain/parties'
import { itemCopy, WIZARD_ERRORS } from '../descriptor/stepCopy'
import { type ContactOption, type WizardContext } from '../domain/types'
import { type LoadedDraftState, type WizardDraft } from '../domain/wizardDraft'

export interface LoadOpeningOptions {
  readOnly: boolean
}

interface SavedOpening {
  openingId: string
  savedMetadata: Record<string, unknown>
}

const guessedConfirmations = (steps: AccountOpeningStep[], loaded: LoadedDraft): Record<AccountOpeningStepId, string[]> => {
  const accountRefs = loaded.accounts.map((account) => account.accountRefId)
  const answeredPartyRefs = loaded.parties.filter((party) => Object.keys(party.kycValues).length > 0).map((party) => party.customerRefId)
  const guessed: Record<AccountOpeningStepId, string[]> = {}
  steps.forEach((step) => {
    if (step.kind === 'collection' && step.confirm) {
      guessed[step.id] = step.collection === AccountOpeningCollections.ACCOUNTS ? accountRefs : answeredPartyRefs
    }
  })
  return guessed
}

export const storedConfirmations = (
  metadata: Record<string, unknown> | undefined,
  steps: AccountOpeningStep[],
  loaded: LoadedDraft,
): Record<AccountOpeningStepId, string[]> => {
  const stored = (metadata as { confirmations?: Record<AccountOpeningStepId, string[]> } | undefined)?.confirmations
  return stored ?? guessedConfirmations(steps, loaded)
}

export const useAccountOpeningPersistence = ({
  ctx,
  descriptor,
  steps,
  accountTypes,
  selectors,
  contactOptions,
  submitStepId,
}: {
  ctx: WizardContext
  descriptor: AccountOpeningDescriptor | undefined
  steps: AccountOpeningStep[]
  accountTypes: AccountType[]
  selectors: AccountOpeningTypeSelector[]
  contactOptions: ContactOption[]
  submitStepId: AccountOpeningStepId | undefined
}) => {
  const [saveError, setSaveError] = useState<string | null>(null)

  const [createAccountOpening, { isLoading: isCreating }] = useCreateAccountOpeningMutation()
  const [updateAccountOpening, { isLoading: isUpdating }] = useUpdateAccountOpeningMutation()

  const save = useCallback(
    async (draft: WizardDraft, overrides?: { status?: AccountOpeningStatus; metadata?: Record<string, unknown> }): Promise<SavedOpening | null> => {
      setSaveError(null)
      const payloadAccounts = buildAccountsPayload(draft.accounts, draft.parties)
      const payloadCustomers = buildCustomersPayload(draft.parties)
      const metadata: Record<string, unknown> = {
        ...draft.savedMetadata,
        ...(overrides?.metadata ?? {}),
        confirmations: draft.confirmations,
        descriptorVersion: descriptor?.version,
      }
      try {
        if (draft.openingId) {
          await updateAccountOpening({
            accountId: ctx.accountId,
            openingId: draft.openingId,
            body: {
              accounts: payloadAccounts,
              customers: payloadCustomers,
              status: overrides?.status,
              metadata,
              operationRuns: draft.operationRuns,
            },
          }).unwrap()
          return { openingId: draft.openingId, savedMetadata: metadata }
        }
        const body: AccountOpeningCreateRequest = {
          connector: ctx.connector,
          sourceRefs: ctx.roomId && ctx.sessionId ? [{ type: 'meeting', roomId: ctx.roomId, sessionId: ctx.sessionId }] : [],
          accounts: payloadAccounts,
          customers: payloadCustomers,
          metadata,
        }
        const response = await createAccountOpening({ accountId: ctx.accountId, body }).unwrap()
        return { openingId: response.data.id, savedMetadata: metadata }
      } catch {
        setSaveError(WIZARD_ERRORS.save)
        return null
      }
    },
    [descriptor, ctx.accountId, ctx.connector, ctx.roomId, ctx.sessionId, createAccountOpening, updateAccountOpening],
  )

  const loadOpening = useCallback(
    (dto: AccountOpeningDTO): LoadedDraftState => {
      const selectionFor = (typeId: string): Record<string, string> => {
        const type = findAccountType(accountTypes, typeId)
        if (!type) {
          return {}
        }
        return Object.fromEntries(selectors.map((selector) => [selector.field, axisValue(type, selector.field)]))
      }
      const resolveContactName = (id: string): string => contactOptions.find((option) => option.id === id)?.name ?? ''
      const loaded = loadDraftIntoWizard(dto, selectionFor, resolveContactName, itemCopy(descriptor).partyFallbackName)
      setSaveError(null)

      const operationResults: Record<AccountOpeningStepId, SubmissionOutcome> =
        dto.status === 'submitted' && submitStepId ? { [submitStepId]: storedOutcome(dto.metadata) } : {}

      return {
        openingId: dto.id,
        accounts: loaded.accounts,
        parties: loaded.parties,
        confirmations: storedConfirmations(dto.metadata, steps, loaded),
        operationResults,
        operationRuns: dto.operationRuns ?? {},
        savedMetadata: (dto.metadata as Record<string, unknown> | undefined) ?? {},
      }
    },
    [accountTypes, selectors, contactOptions, descriptor, steps, submitStepId],
  )

  return {
    saveError,
    clearSaveError: useCallback(() => setSaveError(null), []),
    isSaving: isCreating || isUpdating,
    save,
    loadOpening,
  }
}
